import React from 'react';
import { 
  TrendingUp, 
  Shield, 
  Wallet,
  BarChart3,
  Play,
  CheckCircle,
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  Sparkles,
  Star,
  Zap
} from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_sgficapital/artifacts/i5d6sap9_LOGO%20SGFI-nuevo.png';

// Hero images
const HERO_IMAGES = {
  dashboard: 'https://images.unsplash.com/photo-1660970781103-ba6749cb9ce3?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
  growth: 'https://images.unsplash.com/photo-1579227114496-27346f474519?crop=entropy&cs=srgb&fm=jpg&w=800&q=85'
};

// Icon mapping
const iconMap = {
  TrendingUp,
  Shield,
  Wallet,
  BarChart3,
  CheckCircle,
  Zap,
  Sparkles
};

export default function LandingPreview({ content, scale = 0.5 }) {
  const getIcon = (iconName) => {
    return iconMap[iconName] || TrendingUp;
  };

  if (!content) {
    return (
      <div className="flex items-center justify-center h-full bg-[#0d1218] text-[#93a5af]">
        <p>Cargando preview...</p>
      </div>
    );
  }

  return (
    <div 
      className="origin-top-left bg-[#0d1218] min-h-full"
      style={{ 
        transform: `scale(${scale})`,
        width: `${100 / scale}%`,
        height: `${100 / scale}%`
      }}
    >
      {/* Navigation - Brand Colors */}
      <nav className="bg-[#0d1218]/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <img src={LOGO_URL} alt="SGFI Capital" className="h-8 w-auto" />
            <div className="flex items-center gap-6 text-sm">
              <span className="text-[#93a5af] font-medium">Cómo funciona</span>
              <span className="text-[#93a5af] font-medium">Beneficios</span>
              <span className="text-[#93a5af] font-medium">Testimonios</span>
              <span className="text-[#93a5af] font-medium">Contacto</span>
              <button className="text-white font-medium">Iniciar Sessão</button>
              <button className="bg-[#2e8036] text-white rounded-full px-4 py-2 text-sm font-semibold shadow-lg shadow-[#2e8036]/25">
                Criar Conta
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Brand Colors */}
      <section className="relative py-20 overflow-hidden bg-[#0d1218]">
        {/* Background decoration */}
        <div className="absolute top-1/4 -left-1/4 w-96 h-96 bg-[#2e8036]/15 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-1/4 w-96 h-96 bg-[#93a5af]/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left content */}
            <div>
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#2e8036]/15 border border-[#2e8036]/30 mb-6">
                <Sparkles className="w-3 h-3 text-[#2e8036]" />
                <span className="text-xs font-medium text-[#93a5af]">{content.hero?.badge || 'Plataforma Premium'}</span>
              </div>
              
              {/* Title */}
              <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight tracking-tight mb-4">
                {content.hero?.title || 'Tu dinero,'}
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#2e8036] via-[#3d9945] to-[#93a5af]">
                  {content.hero?.titleHighlight || 'sin límites'}
                </span>
              </h1>
              
              <p className="text-lg text-[#93a5af] mb-8 max-w-lg leading-relaxed">
                {content.hero?.subtitle || 'Únete a miles de inversores.'}
              </p>
              
              {/* CTA Buttons */}
              <div className="flex gap-3">
                <button className="bg-[#2e8036] text-white rounded-full px-6 py-3 text-sm font-semibold flex items-center gap-2 shadow-lg shadow-[#2e8036]/25">
                  {content.hero?.ctaPrimary || 'Empezar ahora'}
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button className="border-2 border-[#93a5af]/30 text-white rounded-full px-6 py-3 text-sm font-medium">
                  {content.hero?.ctaSecondary || 'Saber más'}
                </button>
              </div>

              {/* Trust badges */}
              <div className="flex items-center gap-4 mt-8">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-[#2e8036] to-[#1c262d] border-2 border-[#0d1218]" />
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-xs text-[#93a5af]/70 mt-0.5">+10K inversores</p>
                </div>
              </div>
            </div>

            {/* Right - Hero Image */}
            <div className="relative">
              <div className="relative mx-auto max-w-sm">
                <div className="relative rounded-[2rem] overflow-hidden shadow-2xl shadow-[#2e8036]/20 bg-gradient-to-b from-[#1c262d] to-[#0d1218] p-2">
                  <img 
                    src={HERO_IMAGES.dashboard}
                    alt="App Dashboard"
                    className="rounded-[1.5rem] w-full"
                  />
                </div>
                
                {/* Floating cards */}
                <div className="absolute -left-8 top-1/4 bg-[#1c262d] border border-[#2e8036]/30 rounded-xl shadow-lg shadow-[#2e8036]/10 p-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[#2e8036]/20 flex items-center justify-center">
                      <TrendingUp className="w-4 h-4 text-[#2e8036]" />
                    </div>
                    <div>
                      <p className="text-xs text-[#93a5af]/70">Rendimiento</p>
                      <p className="text-sm font-bold text-white">+4.0%</p>
                    </div>
                  </div>
                </div>
                
                <div className="absolute -right-6 bottom-1/3 bg-[#1c262d] border border-[#93a5af]/20 rounded-xl shadow-lg p-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[#93a5af]/20 flex items-center justify-center">
                      <Shield className="w-4 h-4 text-[#93a5af]" />
                    </div>
                    <div>
                      <p className="text-xs text-[#93a5af]/70">Seguro</p>
                      <p className="text-sm font-bold text-white">100%</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats bar */}
          <div className="mt-16 grid grid-cols-3 gap-6 max-w-2xl mx-auto">
            {(content.hero?.stats || []).map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl font-bold text-white">{stat.value}</p>
                <p className="text-[#93a5af]/70 text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-[#1c262d]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-3 tracking-tight">
              {content.howItWorks?.title || 'Invertir nunca fue tan fácil'}
            </h2>
            <p className="text-lg text-[#93a5af] max-w-2xl mx-auto">
              {content.howItWorks?.subtitle || 'Comienza en minutos'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {(content.howItWorks?.steps || []).map((item, index) => (
              <div 
                key={index}
                className="bg-[#0d1218] rounded-2xl p-6 border border-white/5 hover:border-[#2e8036]/30 transition-all"
              >
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#2e8036] text-white text-sm font-bold mb-4 shadow-lg shadow-[#2e8036]/25">
                  {item.step}
                </span>
                <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-[#93a5af] text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-[#0d1218]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-3 tracking-tight">
              {content.benefits?.title || 'Todo lo que necesitas'}
            </h2>
            <p className="text-lg text-[#93a5af] max-w-2xl mx-auto">
              {content.benefits?.subtitle || 'Una plataforma completa'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {(content.benefits?.items || []).map((benefit, index) => {
              const Icon = getIcon(benefit.icon);
              return (
                <div 
                  key={index}
                  className="bg-[#1c262d] rounded-2xl p-6 border border-white/5 hover:border-[#2e8036]/20 transition-all group"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#2e8036]/10 group-hover:bg-[#2e8036] flex items-center justify-center mb-4 transition-all">
                    <Icon className="w-5 h-5 text-[#2e8036] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-base font-bold text-white mb-2">{benefit.title}</h3>
                  <p className="text-[#93a5af] text-sm">{benefit.description}</p>
                </div>
              );
            })}
          </div>

          {/* Features Checklist */}
          <div className="mt-12 bg-gradient-to-r from-[#2e8036] to-[#247029] rounded-2xl p-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {(content.benefits?.features || []).map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-white flex-shrink-0" />
                  <span className="text-white text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-[#1c262d]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-3 tracking-tight">
              {content.testimonials?.title || 'Lo que dicen nuestros clientes'}
            </h2>
            <p className="text-lg text-[#93a5af] max-w-2xl mx-auto">
              {content.testimonials?.subtitle || 'Miles de inversores confían en nosotros'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {(content.testimonials?.items || []).map((testimonial, index) => (
              <div 
                key={index}
                className="bg-[#0d1218] border border-white/5 rounded-2xl p-6 hover:border-[#2e8036]/30 transition-all"
              >
                {/* Stars */}
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      className={`w-4 h-4 ${i < (testimonial.rating || 5) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'}`}
                    />
                  ))}
                </div>
                
                {/* Quote */}
                <p className="text-[#93a5af] mb-4 text-sm leading-relaxed">
                  "{testimonial.text || 'Excelente plataforma.'}"
                </p>
                
                {/* Author */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2e8036] to-[#1c262d] flex items-center justify-center text-white font-bold text-sm">
                    {(testimonial.name?.charAt(0) || 'U').toUpperCase()}
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">{testimonial.name || 'Cliente'}</p>
                    <p className="text-[#93a5af]/70 text-xs">{testimonial.role || 'Inversor'}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Videos Section */}
      <section className="py-20 bg-[#0d1218]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-3 tracking-tight">
              {content.videos?.title || 'Conoce nuestra plataforma'}
            </h2>
            <p className="text-lg text-[#93a5af] max-w-2xl mx-auto">
              {content.videos?.subtitle || 'Descubre cómo funciona'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {(content.videos?.items || []).map((video, index) => (
              <div 
                key={video.id || index}
                className="bg-[#1c262d] border border-white/5 rounded-2xl overflow-hidden hover:border-[#2e8036]/30 transition-all"
              >
                <div className="aspect-video relative bg-[#0d1218] flex items-center justify-center">
                  {video.embedUrl ? (
                    <div className="w-full h-full bg-[#2e8036]/20 flex items-center justify-center">
                      <span className="text-white text-xs">Video cargado</span>
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-[#2e8036]/30 flex items-center justify-center">
                      <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="text-base font-bold text-white mb-1">{video.title || 'Video'}</h3>
                  <p className="text-[#93a5af] text-sm">{video.description || 'Descripción'}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Green Gradient */}
      <section className="py-20 bg-gradient-to-r from-[#2e8036] to-[#247029]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 tracking-tight">
            {content.cta?.title || 'Pronto Para Começar?'}
          </h2>
          <p className="text-lg text-green-100 mb-8 max-w-2xl mx-auto">
            {content.cta?.subtitle || 'Abre tu cuenta hoy'}
          </p>
          <button className="bg-white text-[#2e8036] rounded-full px-8 py-3 text-sm font-bold shadow-xl flex items-center gap-2 mx-auto">
            {content.cta?.buttonText || 'Criar Conta gratis'}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-[#0d1218]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-white mb-3 tracking-tight">
                {content.contact?.title || 'Tem Dúvidas?'}
              </h2>
              <p className="text-lg text-[#93a5af] mb-8">
                {content.contact?.subtitle || 'Nuestro equipo está disponible.'}
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 rounded-xl bg-[#1c262d] border border-white/5">
                  <div className="w-10 h-10 rounded-xl bg-[#2e8036]/20 flex items-center justify-center">
                    <Mail className="w-4 h-4 text-[#2e8036]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#93a5af]/70">Email</p>
                    <p className="text-sm font-semibold text-white">{content.contact?.email || 'info@sgfi.capital'}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 rounded-xl bg-[#1c262d] border border-white/5">
                  <div className="w-10 h-10 rounded-xl bg-[#2e8036]/20 flex items-center justify-center">
                    <Phone className="w-4 h-4 text-[#2e8036]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#93a5af]/70">Teléfono</p>
                    <p className="text-sm font-semibold text-white">{content.contact?.phone || '+351 XXX XXX XXX'}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 rounded-xl bg-[#1c262d] border border-white/5">
                  <div className="w-10 h-10 rounded-xl bg-[#2e8036]/20 flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-[#2e8036]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#93a5af]/70">Ubicación</p>
                    <p className="text-sm font-semibold text-white">{content.contact?.location || 'Lisboa, Portugal'}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#1c262d] border border-white/5 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-white mb-4">{content.contact?.legalTitle || 'Información legal'}</h3>
              <div className="space-y-3 text-[#93a5af] text-sm">
                <p>{content.contact?.legalText || 'SGFI Capital - Sociedad Gestora.'}</p>
                <p>© {new Date().getFullYear()} SGFI Capital. Todos los derechos reservados.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 bg-[#0d1218] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <img src={LOGO_URL} alt="SGFI Capital" className="h-6 w-auto" />
            <p className="text-[#93a5af]/50 text-xs">
              © {new Date().getFullYear()} SGFI Capital
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
